/**
 * Created by eliorc on 11/2/14.
 */
/**
 * This class represent the chat input line of each engagement.
 *
 * IMPORTANT NOTICE:
 * the CKEditor has a problem working with the way we are working when recovering from refresh (F5),
 * it's element selector need the element on the document, we are working in Document Fragment and therefor the CKEditor cannot initialize correctly.
 * to overcome this limitation we used the 'handleChatsResumed' event that bounded to the 'agent.chatsResumedCount' key
 * this bind will trigger after the DocumentFragment is attached to the document and it's safe to tell the CKEditor to
 * load.
 */
define(function (require) {

    window.lpTag = window.lpTag || {};
    window.lpTag.WebAgent.logicController = window.lpTag.WebAgent.logicController || {};

    var _ = require('underscore');
    var lpUtils = require('utils/lpUtils');
    var ViewController = require('web-agent-framework/ViewController');
    var agentLEManager = require('web-agent-utils/agentLEManager');
    var webAgentUtils = require('web-agent-utils/WebAgentUtils');
    var PreDefinedShortcutsManager = require('web-agent-utils/PreDefinedShortcutsManager');
    var privilegesController = require('web-agent-assets-js/lpPrivilegesController');
    var FormRichTextEditorViewController = require('web-agent-components/FormRichTextEditorViewController');
    var MessagingUtils = require('web-agent-utils/MessagingUtils');

    var AGENT_IS_TYPING_BOUNCE_TIME = 5 * 1000;

    var COBROWSE_MODE_SHARED = 'shared';
    var COBROWSE_MODE_VIEW = 'view';
    var COBROWSE_MODE_FOLLOW = 'follow';

    var RichTextChatInputViewController = ViewController.extend({
        ControllerName: 'RichTextChatInputViewController',

        LPViewMap: {
            chatState: '.lpview_chat_input_state',
            chatInputAnchor: '.lpview_chat_input_anchor',
            chatInput: '.lpview_form_textarea',
            chatInputSendButton: '.lpview_send_msg_button',
            actionButton: '.lpview_action_button',
            actionButtonText: '.lpview_action_button_text',
            preDefinedShortcutsPlaceholder: '.lpview_pre_defined_placeholder',
            retryJoin: '.lpview_join_conversation_retry',
            disableMessageText: '.lpview_chat_input_message_text'
        },

        LPClassesMap: {
            actionButtonEnabled: 'action_button_enabled',
            scroll: 'scroll',
            activeEngagement: 'active_engagement',
            passiveEngagement: 'passive_engagement',
            proactiveEngagement: 'proactive_engagement',
            active: 'active',
            joinError: 'join_error',
            loading: 'loading',
            disableWithMessage: 'disable_with_message'
        },

        LPActionButtonTypes: {
            joinConversation: {
                buttonText: 'joinConversation',
                buttonCallback: 'joinConversationButtonClicked'
            },
            resumeConnection: {
                buttonText: 'resumeConnection',
                buttonCallback: 'startResumeProcess'
            }
        },

        LPLangMap: {
            oldConvDeleted: 'CHATINPUT_disabledOldConverasation'
        },

        initialize: function (constructorData) {
            this.clipboardNotificationHandler = constructorData.clipboardNotificationHandler;
            this.visible = false;
            this.actionButtonBoundCallback = false;
            this.chatId = constructorData.chatId;
            this.visitorId = constructorData.visitorId;
            this.onResize = constructorData.onResize;
            this.customMenuItems = constructorData.customMenuItems;

            this.agentTyping = this.getModelParam('chats.' + this.chatId + '.agentInfo.agentTyping') === 'typing';
            this.chatState = this.getModelParam('chats.' + this.chatId + '.chatInfo.state');
            this.waitingForResumeTimeoutRef = null;
            this.agentTypingBouncerTimeoutRef = null;
            this.preDefinedShortcutsView = this.getFirstElement(this.LPViewMap.preDefinedShortcutsPlaceholder);
            this.actionButtonTextView = this.getFirstElement(this.LPViewMap.actionButtonText);
            this.disableMessageTextView = this.getFirstElement(this.LPViewMap.disableMessageText);
            this.isFlowStarted = false;
            this.engagementType = window.lpTag.WebAgent.logicController.getEngagementTypeByChatId(this.chatId);

            this.chatInputComponent = new FormRichTextEditorViewController(this.getChatInputConfig());
            this.cannedResponse = {};
            this._resumeState = false;

            var badges = this.getModelParam('chats.' + this.chatId + '.chatInfo.badges');
            this.isInSurvey = !!(badges && badges.in_survey);
            this.isNoActiveDialog = !!(badges && badges.no_active_dialog);

            this.addViewController({
                viewController: this.chatInputComponent,
                target: this.LPViewMap.chatInputAnchor,
                beforeView: this.LPViewMap.chatInputSendButton
            });
        },

        events: function () {
            this.bindModel({modelPath: 'agent.agentName', callback: this.updateAgentName});
            // Setting the "trigger" prop here is necessary since we don't want the initial trigger upon setting the "acceptedEngagement" value.
            // The component will know how to render itself according the chat state, we need this bind just to overcome the scenario where we
            // spectate an engagement and then this engagement is transferred and accepted by us.
            this.bindModel({
                modelPath: 'participants.myEngagements.' + this.visitorId + '.participantInfo.acceptedEngagement',
                callback: this.handleAcceptedEngagement,
                trigger: false
            });

            this.viewEventsHandler.bindEvent({
                appName: this.visitorId,
                callback: this.addPreDefinedContent,
                UIEvent: this.viewEventsHandler.eventType.PRE_DEFINED_CONTENT_SELECTED
            });
            this.viewEventsHandler.bindEvent({
                appName: this.visitorId,
                callback: this.lpFocus,
                UIEvent: this.viewEventsHandler.eventType.VISITOR_FOCUSED
            });

            this.bindModel({modelPath: "agent.chatsResumedCount", callback: this.handleChatsResumed});
            this.registerSendButtonClickEvent();

            if (lpTag.WebAgent.logicController.hasCoBrowseFeature()) {
                agentLEManager.onLEEvent("lpCoBrowseController:Notification", this.onCoBrowseNotification, this);
            }

            this.bindModel({modelPath: "agent.connectionState", callback: this.handleConnectionStatusChanged});
            this.bindModel({modelPath: "amsService.socketState", callback: this.handleConnectionStatusChanged});
            this.bindModel({modelPath: 'chats.' + this.chatId + '.chatInfo.badges', callback: this.onBadgeChanged});

            // Participants Change
            this.bindModel({
                modelPath: 'participants.myEngagements.' + this.visitorId + '.agentInfo.allParticipantsHash',
                callback: this.handleParticipantsChange
            });

            // Retry Join Conversation
            this.bindViewEvent({
                selector: this.LPViewMap.retryJoin,
                DOMEvent: 'click',
                callback: this.joinConversationButtonClicked.bind(this)
            });
        },

        render: function () {
        }
    });


    RichTextChatInputViewController.prototype.getChatInputConfig = function () {
        // Default Config

        var chatInputConstructorData = {
            events: {
                keyPress: this.captureChatInputKeyPress.bind(this),
                keyUp: this.captureChatInputKeyUp.bind(this),
                resize: this.onChatInputResize.bind(this),
                change: this.onChatInputChange.bind(this),
                load: this.onChatInputComponentReady.bind(this)
            },
            settings: {
                height: null,
                // todo - import RichTextEditor! is it still global?
                toolbar: lpUtils.cloneObject(RichTextEditor.TOOLBARS.BASIC),
                capabilities: {
                    clipboard: {
                        enabled: true,
                        mode: 'html',
                        filter: 'processFullHtml',
                        notificationHandler: this.clipboardNotificationHandler
                    }
                },
                plugins: {
                    bidi: {
                        enabled: true
                    },
                    smiley: {
                        enabled: true,
                        mode: 'smiley',
                        config: lpTag.WebAgent.smileyEmojisController.getConfig('smiley')
                    }
                }
            },
            customMenuItems: this.customMenuItems
        };

        switch(this.engagementType){
            case  window.lpTag.WebAgent.logicController.engagementTypes.realTimeChat:
                if(window.lpTag.WebAgent.logicController.hasCoBrowseFeature('web')){
                    var coBrowsePermissions = {
                        agentChoose: LE.siteSettings.cobrowseSettings.getAgentCanChooseProfile(),
                        permission: LE.siteSettings.cobrowseSettings.getProfile()
                    };

                    // 1. Check SiteSettings
                    var hasReadPermissions = coBrowsePermissions.agentChoose || (coBrowsePermissions.permission && coBrowsePermissions.permission !== 'p-interactive');
                    var hasSharedPermissions = coBrowsePermissions.agentChoose || (coBrowsePermissions.permission && coBrowsePermissions.permission === 'p-interactive');

                    // 2. Check User Role
                    if (lpTag.WebAgent.logicController.hasProfileFeature()) {
                        hasReadPermissions = hasReadPermissions && (privilegesController.hasCoBrowseViewOnlyPrivilege() || privilegesController.hasCoBrowseViewOnlyWithScrollPrivilege());
                        hasSharedPermissions = hasSharedPermissions && privilegesController.hasCoBrowseSharedPrivilege();
                    }

                    var coBrowseMenuItems = this.getCoBrowseMenuItems(hasReadPermissions, hasSharedPermissions);
                    if (coBrowseMenuItems && coBrowseMenuItems.length) {
                        chatInputConstructorData.settings.plugins.cobrowse = {
                            enabled: true,
                            menuItems: coBrowseMenuItems
                        };
                        chatInputConstructorData.settings.toolbar.push(RichTextEditor.TOOLBAR_ITEMS.DEVIDER);
                        chatInputConstructorData.settings.toolbar.push(RichTextEditor.TOOLBAR_ITEMS.COBROWSE);
                    }
                }
                break;

            case window.lpTag.WebAgent.logicController.engagementTypes.asyncMessaging:
                chatInputConstructorData.settings.toolbar = lpUtils.cloneObject(RichTextEditor.TOOLBARS.EMPTY);

                chatInputConstructorData.keystrokes = chatInputConstructorData.blockedKeystrokes = [
                    CKEDITOR.CTRL + "I".charCodeAt(0),
                    CKEDITOR.CTRL + "L".charCodeAt(0),
                    CKEDITOR.CTRL + "B".charCodeAt(0),
                    CKEDITOR.CTRL + "U".charCodeAt(0)
                ];

                if (chatInputConstructorData.settings.capabilities.clipboard && chatInputConstructorData.settings.capabilities.clipboard.enabled) {
                    chatInputConstructorData.settings.capabilities.clipboard.filter = 'processOnlyImgHtml';
                }

                chatInputConstructorData.settings.plugins.smiley = {
                    enabled: true,
                    mode: 'emoji',
                    config: lpTag.WebAgent.smileyEmojisController.getConfig('emoji')
                };
                break;
        }

        if(!isSmileyToolbarDisabled(this.engagementType)){
            chatInputConstructorData.settings.toolbar.push(RichTextEditor.TOOLBAR_ITEMS.SMILEY);
        }

        return chatInputConstructorData;

        function isSmileyToolbarDisabled(engagementType){
            switch(engagementType){
                case  window.lpTag.WebAgent.logicController.engagementTypes.realTimeChat:
                    return lpTag.WebAgent.logicController.isIconsForRealTimeDisabled();
                case  window.lpTag.WebAgent.logicController.engagementTypes.asyncMessaging:
                    return lpTag.WebAgent.logicController.isIconsForMessagingisabled();
            }
        }
    };

    /** TODO: New UI - Move this into the Engagement Logic, this is not part of the Text Editor Logic.  */
    RichTextChatInputViewController.prototype.handleParticipantsChange = function(){
        if(!this.chatState || this.chatState === lpTag.WebAgent.logicController.chatStates.ended){
            return;
        }

        var isMyChat = lpTag.WebAgent.logicController.isMyChat(this.chatId);
        var activeDialogId = lpTag.WebAgent.logicController.getActiveDialogIdOfConversation(this.chatId);
        var isManagerParticipant = lpTag.WebAgent.logicController.isLoggedInAgentManagerParticipant(this.chatId, activeDialogId);
        var mainDialog = lpTag.WebAgent.logicController.getMainDialogOfConversation(this.chatId);
        var isMainDialogActive = mainDialog && mainDialog.dialogId === activeDialogId;

        // getting the info about the current user.
        if(!isMyChat && isMainDialogActive) {
            if(isManagerParticipant){
                var isChatInputComponentActive = this.chatInputComponent.richTextEditorState.loaded && this.chatInputComponent.visible;
                if( ! isChatInputComponentActive) {
                    this.initChatInputComponent(true);
                }
            } else if (privilegesController.hasParticipatePrivilege()) {
                // join button is disabled for spectating suggested participants. might be available in a later phase
                if (lpTag.WebAgent.logicController.isParticipantSuggestedInConversation(this.chatId, this.getModelParam('agent.id'))) {
                    this.disable(this.translate('transferToAgent.join.disabled'));
                } else {
                    this.enableActionButton(this.LPActionButtonTypes.joinConversation);
                }
            } else {
                this.initChatInputComponent(false);
            }
        }
    };

    RichTextChatInputViewController.prototype.updateCoBrowseButtonState = function () {
        if (this.chatInputComponent) {
            this.chatInputComponent.setButtonActiveMode('cobrowse', this.isCoBrowseServiceEnabled());
        }
    };

    RichTextChatInputViewController.prototype.onCoBrowseNotification = function (message) {
        if (message && message.data && message.data.state) {
            this.updateCoBrowseButtonState();
        }
    };

    RichTextChatInputViewController.prototype.isCoBrowseServiceEnabled = function () {
        return lpTag.WebAgent.logicController.hasCoBrowseFeature() && lpTag.WebAgent.coBrowseController.canSendInvite();
    };

    RichTextChatInputViewController.prototype.getCoBrowseMenuItems = function (hasReadPermissions, hasSharedPermissions) {
        var self = this;
        var menuItems = [];

        // determine which profile we need
        var viewOnlyProfile = COBROWSE_MODE_VIEW;
        if (lpTag.WebAgent.logicController.hasProfileFeature() &&
            privilegesController.hasCoBrowseViewOnlyPrivilege() &&
            !privilegesController.hasCoBrowseViewOnlyWithScrollPrivilege()) {

            viewOnlyProfile = COBROWSE_MODE_FOLLOW;
        }

        if (hasReadPermissions) {
            menuItems.push({
                icon: 'cobrowse_viewonly',
                title: this.translate('COBROWSE_MENU_ITEM_TITLE_VIEW_ONLY'),
                description: this.translate('COBROWSE_MENU_ITEM_DESCRIPTION_VIEW_ONLY'),
                active: true,
                action: function () {
                    self.sendCoBrowseInvite.call(self, viewOnlyProfile);
                }
            });
        }
        if (hasSharedPermissions) {
            menuItems.push({
                icon: 'cobrowse_shared',
                title: this.translate('COBROWSE_MENU_ITEM_TITLE_SHARED'),
                description: this.translate('COBROWSE_MENU_ITEM_DESCRIPTION_SHARED'),
                active: true,
                action: function () {
                    self.sendCoBrowseInvite.call(self, COBROWSE_MODE_SHARED);
                }
            });
        }

        return menuItems;
    };

    RichTextChatInputViewController.prototype.sendCoBrowseInvite = function (mode) {
        var self = this;
        var translationKey = mode === COBROWSE_MODE_SHARED ? 'COBROWSE_INVITE_MESSAGE_shared': 'COBROWSE_INVITE_MESSAGE_view';

        var params = {
            serviceId: lpTag.WebAgent.coBrowseController.generateServiceId(this.visitorId, this.chatId),
            chatId: this.chatId,
            visitorId: this.visitorId,
            mode: mode,
            engagementType: 'web'
        };

        lpTag.WebAgent.coBrowseController.createServiceId(params, createServiceIdCallback);

        function createServiceIdCallback(err) {
            if (err) {
                return;
            }

            var template = require("web-agent-templates/LP_coBrowseInviteTemplate.html");
            // var template = lpUtils.loadResource("web-agent-templates/LP_coBrowseInviteTemplate.html");
            var inviteOffer = self.templateString(template, {
                accept: self.translate('COBROWSE_INVITE_ACCEPT_BUTTON'),
                reject: self.translate('COBROWSE_INVITE_REJECT_BUTTON'),
                serviceId: params.serviceId,
                message: self.translate(translationKey)
            }, false);

            self.modelController.addLine({
                chatId: self.chatId,
                source: 'agent',
                by: self.agentName,
                textType: 'html',
                text: inviteOffer,
                time: new Date()
            });
        }
    };

    RichTextChatInputViewController.prototype.updateChatId = function (chatId) {
        // terminating the binding if the flow was not started yet, only update the chatId, the 'handleChatsResumed'
        // method will fire when the view is in the DOM and will bootstrap the loading process.
        if (!this.isFlowStarted) {
            this.chatId = chatId;
            return;
        }

        if (this.chatId) {
            this.unbindModel({modelPath: 'chats.' + this.chatId + '.chatInfo.badges', callback: this.onBadgeChanged});
            this.unbindModel({modelPath: 'chats.' + this.chatId + '.chatInfo.state', callback: this.handleChatStateChanged});
        }
        this.chatId = chatId;
        this.bindModel({modelPath: 'chats.' + this.chatId + '.chatInfo.badges', callback: this.onBadgeChanged});
        this.bindModel({modelPath: 'chats.' + this.chatId + '.chatInfo.state', callback: this.handleChatStateChanged});
    };

    RichTextChatInputViewController.prototype.handleChatsResumed = function () {
        this.isFlowStarted = true;
        this.bindModel({modelPath: 'chats.' + this.chatId + '.chatInfo.state', callback: this.handleChatStateChanged});
    };

    RichTextChatInputViewController.prototype.renderChatComponent = function (callback) {
        if (this.actionButtonEnabled) {
            return;
        }
        if (!this.chatInputComponent.isRendered()) {
            this.chatInputComponent.renderComponent(callback);
        } else if (callback) {
            callback();
        }
    };

    RichTextChatInputViewController.prototype.onChatInputResize = function (data) {
        if (this.onResize) {
            this.onResize(data.diff);
        }
    };

    RichTextChatInputViewController.prototype.onChatInputComponentReady = function () {
        this.updateAgentTypingState();
    };

    RichTextChatInputViewController.prototype.onChatInputChange = function () {
        this.shouldShowSendButtonAsActive();
        this.updateAgentTypingState();
    };

    RichTextChatInputViewController.prototype.handleConnectionStatusChanged = function (eventData) {
        this.shouldShowSendButtonAsActive();
        this.shouldDisableSendButtonAction();
    };

    RichTextChatInputViewController.prototype.isConnectionActive = function () {
        // todo - extract this functionallity for reuse, see similar function in EngagementViewController
        var active;
        if (lpTag.WebAgent.logicController.isEngagementAsyncMessaging(this.chatId)) {
            active = lpTag.WebAgent.logicController.isAmsConnectionStatusActive();
        } else if (lpTag.WebAgent.logicController.isEngagementRealTimeChat(this.chatId)) {
            active = lpTag.WebAgent.logicController.isConnectionStatusActive();
        }
        return !!active;
    };

    RichTextChatInputViewController.prototype.shouldShowSendButtonAsActive = function () {
        var isValidText = !this.chatInputComponent.isEmpty();

        if (isValidText && this.isConnectionActive()) {
            this.addClass(this.LPViewMap.chatInputSendButton, this.LPClassesMap.active);
        } else {
            this.removeClass(this.LPViewMap.chatInputSendButton, this.LPClassesMap.active);
        }
    };

    RichTextChatInputViewController.prototype.shouldDisableSendButtonAction = function () {
        if (this.isConnectionActive()) {
            this.unregisterSendButtonClickEvent();
            this.registerSendButtonClickEvent();
            this.setSendButtonTooltip("");
        } else {
            this.unregisterSendButtonClickEvent();

            //There is a case in RT when the chat is not active and the connectivity is lost.
            //The agent has no way to send chat lines, so we won't show the tooltip. Otherwise show it.
            if ((lpTag.WebAgent.logicController.getEngagementTypeByChatId(this.chatId) === lpTag.WebAgent.logicController.engagementTypes.realTimeChat) &&  //if RT chat and
                (this.getModelParam('chats.' + this.chatId + '.chatInfo.state') !== lpTag.WebAgent.logicController.chatStates.chatting)) { //if status is not chatting
                this.setSendButtonTooltip("");
            } else {
                this.setSendButtonTooltip(this.translate("connectionProblemSendLineTooltip"));
            }
        }
    };

    RichTextChatInputViewController.prototype.registerSendButtonClickEvent = function () {
        this.bindViewEvent({
            selector: this.LPViewMap.chatInputSendButton,
            DOMEvent: 'click',
            callback: this.captureSendButtonClick
        });
    };
    RichTextChatInputViewController.prototype.unregisterSendButtonClickEvent = function () {
        this.unbindViewEvent({
            selector: this.LPViewMap.chatInputSendButton,
            DOMEvent: 'click',
            callback: this.captureSendButtonClick
        });
    };

    RichTextChatInputViewController.prototype.setSendButtonTooltip = function (text) {
        var button = this.getFirstElement(this.LPViewMap.chatInputSendButton);
        if (button) {
            if (text) {
                button.title = text;
            } else {
                button.removeAttribute("title");
                button.removeAttribute("tooltip-text");
            }
        }
    };

    RichTextChatInputViewController.prototype.handleChatStateChanged = function (data) {
        this.chatState = data.newValue;
        this._resumeState = false;
        if (this.chatState === lpTag.WebAgent.logicController.chatStates.ended) {
            this.resetCannedResponseData();
            //lpTag.WebAgent.logicController.isAcceptedEngagement(this.visitorId) <-- Removed to allowed the agent manager to resume a chat.
            if (lpTag.WebAgent.logicController.isEngagementAsyncMessaging(this.chatId)) {
                this.enableActionButton(this.LPActionButtonTypes.resumeConnection);
            } else {
                this.initChatInputComponent(false);
            }
        } else if (this.chatState === lpTag.WebAgent.logicController.chatStates.chatting) {
            var activeDialogId = lpTag.WebAgent.logicController.getActiveDialogIdOfConversation(this.chatId);
            if (this.isInSurvey || this.isNoActiveDialog) {
                this.disable(this.isInSurvey ? this.translate('survey_in_progress') : '');
            } else if (lpTag.WebAgent.logicController.isAcceptedEngagement(this.visitorId) || // did I accept the engagement? RT question
                lpTag.WebAgent.logicController.isLoggedInAgentManagerParticipant(this.chatId, activeDialogId)) { // only for messaging, and only for JOIN. (reader(opened conv), peeker(widget) or manager(joined))
                this.initChatInputComponent(true);
            } else {
                if (privilegesController.hasParticipatePrivilege()) {
                    if (lpTag.WebAgent.logicController.isParticipantSuggestedInConversation(this.chatId, this.getModelParam('agent.id'))) {
                        this.disable(this.translate('transferToAgent.join.disabled'));
                    } else {
                        this.enableActionButton(this.LPActionButtonTypes.joinConversation);
                    }
                } else {
                    this.initChatInputComponent(false);
                }
            }
        }
    };

    RichTextChatInputViewController.prototype.hasJoinedConversation = function () {
        return this.joinedConversation;
    };

    RichTextChatInputViewController.prototype.isOnResumeState = function () {
        return !!this._resumeState;
    };

    RichTextChatInputViewController.prototype.resetCannedResponseData = function () {
        this.cannedResponse = {};
    };

    RichTextChatInputViewController.prototype.handleAcceptedEngagement = function (data) {
        if (!data.newValue) {
            return;
        }
        this.initChatInputComponent(true);
    };

    RichTextChatInputViewController.prototype.onBadgeChanged = function (data) {
        var badges = data.newValue;
        var in_survey_badge = !!badges.in_survey;
        var no_active_badge = !!badges.no_active_dialog;
        var needToRefresh = (this.isInSurvey !== in_survey_badge) || (this.isNoActiveDialog !== no_active_badge);

        if (needToRefresh) {
            this.isInSurvey = in_survey_badge;
            this.isNoActiveDialog = no_active_badge;
            this.refreshChatState();
        }
    };

    RichTextChatInputViewController.prototype.captureChatInputKeyPress = function (e) {
        function onSendChatLineDone() {
            this.touchSession();
            this.setAgentTypingState(false);
            this.lpFocus();
        }

        if ((e.keyCode === 13) && !this.preDefinedShortcutsPopupVisible) {
            e.event.cancel();
            this.sendChatLine(onSendChatLineDone.bind(this));
        }
    };

    /**
     * Preventing defaults and sending the line on keyup of 'Enter'
     * Updating AgentIsTyping as well
     * @param e
     */
    RichTextChatInputViewController.prototype.captureChatInputKeyUp = function (e) {
        var keyCode = e.keyCode;
        var chatInputValue = this.chatInputComponent.getTextValue();

        chatInputValue = chatInputValue.replace(/\s+$/ig, '');

        if (keyCode == 39 && chatInputValue.length === 1) { // Only one char can trigger a shortcut for now
            this.preDefinedShortcutsPopupVisible = true;
            this.viewEventsHandler.fireEvent({
                appName: PreDefinedShortcutsManager.applicationType,
                UIEvent: this.viewEventsHandler.eventType.PRE_DEFINED_CONTENT_SHORTCUT,
                eventData: {
                    visitorInfo: {
                        visitorId: this.visitorId,
                        targetView: this.preDefinedShortcutsView,
                        focusedView: this.chatInputComponent.getEditorContainer()
                    },
                    shortcutInfo: {
                        key: chatInputValue
                    }
                },
                deleteOnFire: true
            });
        }


    };


    RichTextChatInputViewController.prototype.captureSendButtonClick = function () {
        function onSendChatLineDone() {
            this.setAgentTypingState(false);
            this.lpFocus();
        }

        this.sendChatLine(onSendChatLineDone.bind(this));
    };

    /**
     * this func updates the 'AgentIsTyping' notification while avoiding white spaces
     */
    RichTextChatInputViewController.prototype.updateAgentTypingState = function () {
        var isTyping = !this.chatInputComponent.isEmpty();
        this.setAgentTypingState(isTyping);
    };

    /**
     * this func add a predefinedContent text to chat text box
     * @param data
     */
    RichTextChatInputViewController.prototype.addPreDefinedContent = function (data) {
        if (this.visible) {
            if (!data) {
                this.logger.error('Error! Empty preDefinedContent selected.', 'addPreDefinedContent');
            } else if (typeof data.newValue != 'undefined') {
                var preDefinedContentItem = data.newValue.eventData && data.newValue.eventData.text;
                if (preDefinedContentItem) { // We will set the editor data with new content only if we received a replacement text from the shortcuts
                    this.preDefinedContentCurrentItem = preDefinedContentItem;
                    this.createCannedResponseDataForAddLineEvent(data.newValue.eventData);
                    if (data.newValue.eventData.replace) { // This indicates that we got here from the predefined shortcuts
                        this.chatInputComponent.clear();
                    }

                    if (this.engagementType === lpTag.WebAgent.logicController.engagementTypes.asyncMessaging) {
                        this.chatInputComponent.insertHTML({
                            html: preDefinedContentItem,
                            convertToPlainText: true
                        });
                    } else {
                        this.chatInputComponent.insertHTML({
                            html: preDefinedContentItem,
                            sanitizeFunc: this.sanitizeText.bind(this),
                            sanitationType: 'KEEP_AND_SHOW_WARNING',
                            sanitizeMessage: this.translate('RICH_TEXT_CHAT_INPUT_sanitizeMessage')
                        });
                    }


                    this.chatInputComponent.lpFocus();

                }
            }
        }
        this.preDefinedShortcutsPopupVisible = false;
    };

    RichTextChatInputViewController.prototype.createCannedResponseDataForAddLineEvent = function (obj) {
        this.cannedResponse = {
            "cannedAnswerId": obj.currentLineId,
            "cannedTreeId": obj.categoryId
        };
    };

    RichTextChatInputViewController.prototype.enableActionButton = function (actionItemTypeObject) {
        if (actionItemTypeObject) {
            this.removeClass(this.LPViewMap.chatState, this.LPClassesMap.disableWithMessage);
            this.disableMessageTextView.textContent = "";

            this.actionButtonEnabled = true;
            this.actionButtonTextView.textContent = this.translate(actionItemTypeObject.buttonText);
            this.addClass(this.LPViewMap.chatState, this.LPClassesMap.actionButtonEnabled);
            this.chatInputComponent.lpHide();
            this.toggleChatInputComponents(false);
            this.rebindActionButton(actionItemTypeObject);

            if (actionItemTypeObject === this.LPActionButtonTypes.joinConversation) {
                this.joinedConversation = false;
            }
        }
    };

    RichTextChatInputViewController.prototype.rebindActionButton = function (actionItemTypeObject) {
        this.unbindActionButton();
        this.actionButtonBoundCallback = _.bind(this.onActionButtonClick, this, actionItemTypeObject);
        this.bindViewEvent({
            selector: this.LPViewMap.actionButton,
            DOMEvent: 'click',
            callback: this.actionButtonBoundCallback
        });
    };

    RichTextChatInputViewController.prototype.unbindActionButton = function () {
        if (this.actionButtonBoundCallback) {
            this.unbindViewEvent({
                selector: this.LPViewMap.actionButton,
                DOMEvent: 'click',
                callback: this.actionButtonBoundCallback
            });
            delete this.actionButtonBoundCallback;
        }
    };

    RichTextChatInputViewController.prototype.onActionButtonClick = function (actionItemTypeObject, e) {
        var callback = actionItemTypeObject.buttonCallback;
        this.actionButtonEnabled = false;
        if (actionItemTypeObject === this.LPActionButtonTypes.joinConversation) {
            this.joinedConversation = true;
        }

        if (callback && this[callback] && _.isFunction(this[callback])) {
            this[callback]();
        }
    };

    RichTextChatInputViewController.prototype.startResumeProcess = function () {
        this._resumeState = true;
        this.initChatInputComponent(true);
        this.addClass(this.LPViewMap.chatState, this.LPClassesMap.proactiveEngagement);
    };

    /**
     * Disable the text editor
     * @param {String=} message message to show on disabled editor
     */
    RichTextChatInputViewController.prototype.disable = function(message) {
        this.removeClass(this.LPViewMap.chatState, this.LPClassesMap.loading);
        this.removeClass(this.LPViewMap.chatState, this.LPClassesMap.actionButtonEnabled);
        this.addClass(this.LPViewMap.chatState, this.LPClassesMap.disableWithMessage);
        this.disableMessageTextView.textContent = message || '';
        this.chatInputComponent.lpHide();
    };

    RichTextChatInputViewController.prototype.joinConversationButtonClicked = function () {
        var isAsyncMessaging = lpTag.WebAgent.logicController.isEngagementAsyncMessaging(this.chatId);
        var isSpectated = lpTag.WebAgent.logicController.isSpectatedEngagementByChatId(this.chatId);
        var timeoutRef = null;
        if (isAsyncMessaging && isSpectated) {
            lpTag.WebAgent.AppMediator.updateManagerParticipant({
                conversationId: this.chatId,
                dialogId: lpTag.WebAgent.logicController.getActiveDialogIdOfConversation(this.chatId),
                error: onParticipantFailed.bind(this)
            });
            this.addClass(this.LPViewMap.chatState, this.LPClassesMap.loading);
            timeoutRef = setTimeout(onJoinTimeOut.bind(this), 10 * 1000);
        } else {
            this.initChatInputComponent(true);
        }

        function onParticipantFailed(){
            // remove loading state
            this.removeClass(this.LPViewMap.chatState, this.LPClassesMap.loading);
            // Show Error State.
            this.addClass(this.LPViewMap.chatState, this.LPClassesMap.joinError);

            // Removing the timeout.
            clearTimeout(timeoutRef);
        }

        function onJoinTimeOut(){
            if(this.hasClass(this.LPViewMap.chatState, this.LPClassesMap.loading)[0] === true){
                onParticipantFailed.call(this);
            }
        }
    };

    RichTextChatInputViewController.prototype.initChatInputComponent = function (isActive) {
        this.actionButtonEnabled = false;
        this.joinedConversation = true;
        this.removeClass(this.LPViewMap.chatState, this.LPClassesMap.actionButtonEnabled);
        this.removeClass(this.LPViewMap.chatState, this.LPClassesMap.disableWithMessage);
        this.disableMessageTextView.textContent = "";

        this.renderChatComponent(onRenderChatComponentComplete.bind(this));

        function onRenderChatComponentComplete() {
            this.toggleChatInputComponents(isActive, false, onToggleChatInputDone.bind(this, isActive));
            this.toggleSendButtonVisibility(true);
            this.updateCoBrowseButtonState();
        }

        function onToggleChatInputDone(isActive) {
            this.chatInputComponent.lpShow();
            if (isActive) {
                this.removeClass(this.LPViewMap.chatState, this.LPClassesMap.loading);
                this.removeClass(this.LPViewMap.chatState, this.LPClassesMap.joinError);
                this.chatInputComponent.clear();
                this.chatInputComponent.lpFocus();
            }

            // this is the simple place to handle the update of the buttons when the chat state has change, we re-check the
            // state thet the button should be and re-activate / de-activate it.
            this.updateCoBrowseButtonState();
        }
    };

    RichTextChatInputViewController.prototype.toggleChatInputComponents = function (activate, preserveText, callBack) {
        if (activate || (!activate && preserveText)) {
            this.chatInputComponent.setEnabled(activate);
            this.visible = true;
            if (callBack) {
                callBack();
            }
        }
        else {
            this.chatInputComponent.setValue('', onSetValueDone.bind(this, activate, callBack));
        }

        function onSetValueDone() {
            this.chatInputComponent.setEnabled(false);
            this.visible = false;
            if (callBack) {
                callBack();
            }
        }
    };

    RichTextChatInputViewController.prototype.setAgentTypingState = function (newState) {
        var isAcceptedEngagement = lpTag.WebAgent.logicController.isAcceptedEngagement(this.visitorId);
        var activeDialogId = lpTag.WebAgent.logicController.getActiveDialogIdOfConversation(this.chatId);
        var isParticipatingAsManager = lpTag.WebAgent.logicController.isLoggedInAgentManagerParticipant(this.chatId, activeDialogId);
        if (this.visible && (isAcceptedEngagement || isParticipatingAsManager) &&
            this.chatState === lpTag.WebAgent.logicController.chatStates.chatting) {
            if (newState !== this.agentTyping) {
                this.agentTyping = newState;
                this.modelController.setAgentTyping({
                    chatId: this.chatId,
                    typing: this.agentTyping
                });


            }
            if (this.agentTyping) {
                this.scheduleResetOfAgentIsTyping();
            }
        }
    };

    RichTextChatInputViewController.prototype.scheduleResetOfAgentIsTyping = function () {
        if (this.agentTypingBouncerTimeoutRef) {
            clearTimeout(this.agentTypingBouncerTimeoutRef);
        }

        this.agentTypingBouncerTimeoutRef = setTimeout(resetAgentTyping.bind(this), AGENT_IS_TYPING_BOUNCE_TIME);

        function resetAgentTyping() {
            if (this.agentTyping) {
                this.setAgentTypingState(false);
            }
        }
    };

    RichTextChatInputViewController.prototype.refreshChatState = function () {
        var chatState = this.getModelParam('chats.' + this.chatId + '.chatInfo.state');
        this.handleChatStateChanged({newValue: chatState});
    };

    RichTextChatInputViewController.prototype.sendChatLine = function (cb) {
        var chatLineObj = {
            chatId: this.chatId
        };

        // Check that there is text to send, and that the status of the connections is online
        var isEmpty = this.chatInputComponent.isEmpty();
        var isConnectionStatusActive = this.isConnectionActive();

        if (!isEmpty && isConnectionStatusActive) {
            _setTextTypeAndValue.call(this);
            if (_isResumeConnection.call(this)) {
                _createConversationWithChatLine.call(this, this.getModelParam('chats.' + this.chatId + '.chatInfo.skillIds'));
            } else {
                _addChatLine.call(this);
                this.chatInputComponent.clear(_onSetValueDone.bind(this, cb));
            }
        }


        function _setTextTypeAndValue() {
            if (lpTag.WebAgent.logicController.isEngagementAsyncMessaging(this.chatId)) {
                chatLineObj.textType = "text/plain or img";
                chatLineObj.textValue = this.chatInputComponent.getTextValue(lpTag.WebAgent.smileyEmojisController.replaceImagesByEmojiCodes);
            } else {
                chatLineObj.textType = "html";
                chatLineObj.textValue = this.chatInputComponent.getValue(false, true, true);
            }

            setCurrentPreDefinedContentState.call(this);
        }

        function setCurrentPreDefinedContentState() {
            if (!_.isEmpty(this.cannedResponse)) {
                this.cannedResponse.edited = this.preDefinedContentCurrentItem && this.chatInputComponent.getValue(true, true, false) !== lpUtils.stripHTML(this.preDefinedContentCurrentItem);
            }
        }

        function _isResumeConnection() {
            return (lpTag.WebAgent.logicController.isEngagementAsyncMessaging(this.chatId) &&
            this.getModelParam('chats.' + this.chatId + '.chatInfo.state') === lpTag.WebAgent.logicController.chatStates.ended);
        }

        function _createConversationWithChatLine(skillId) {
            var context = this;
            var originContext = this.getModelParam('chats.' + this.chatId + '.chatInfo.originContext');
            // Adding the Campaign information if has any.
            var engagementId  = this.getModelParam('chats.' + this.chatId + '.chatInfo.engagementId');
            var campaignId    = this.getModelParam('chats.' + this.chatId + '.chatInfo.campaignId');

            var campaignInfo;
            if(engagementId && campaignId){
                campaignInfo = {
                    engagementId: engagementId,
                    campaignId: campaignId
                };
            }

            this.modelController.resumeConversation({
                conversationId: this.chatId,
                originContext: originContext,
                consumerId: this.visitorId,
                skillId: skillId || '-1',
                campaignInfo: campaignInfo,
                cannedResponse: this.cannedResponse,
                agentName: this.agentName,
                error: function (errorData) {
                    context.onResumeError(
                        errorData,
                        _createConversationWithChatLine.bind(context),
                        context.refreshChatState.bind(context),
                        context.refreshChatState.bind(context)
                    );
                },
                newChatLine: chatLineObj
            });
        }

        function _addChatLine() {
            this.modelController.addLine({
                chatId: chatLineObj.chatId,
                source: 'agent',
                by: this.agentName,
                textType: chatLineObj.textType,
                text: chatLineObj.textValue,
                time: new Date(),
                cannedResponse: this.cannedResponse
            });

            this.resetCannedResponseData();
        }

        function _onSetValueDone(cb) {
            if (cb) {
                cb();
            }
        }
    };

    RichTextChatInputViewController.prototype.onResumeError = function (errorData, retryCallback, cancelChatInputState, dismissChatInputState) {
        this.logger.error('Could not resume conversation ' + JSON.stringify(errorData));
        this.showDialog({
            title: this.translate('RESUME_CONNECTION_errorTitle'),
            description: this.translate('RESUME_CONNECTION_errorDescription'),
            okButton: this.translate('retryButtonTitle'),
            cancelButton: this.translate('cancel'),
            okCallback: retryCallback,
            cancelCallback: cancelChatInputState,
            dismissCallback: dismissChatInputState,
            targetView: window.MainViewControllerBody,
            modal: true
        });
    };

    RichTextChatInputViewController.prototype.toggleSendButtonVisibility = function (isVisible) {
        if (isVisible) {
            this.addClass(this.LPViewMap.chatInputSendButton, 'visible');
        }
        else {
            this.removeClass(this.LPViewMap.chatInputSendButton, 'visible');
        }
    };

    RichTextChatInputViewController.prototype.updateAgentName = function (data) {
        if (data.newValue) {
            this.agentName = data.newValue;
        }
    };

    RichTextChatInputViewController.prototype.lpFocus = function () {
        if (this.visible) {
            this.chatInputComponent.lpFocus(true);
        }
    };

    RichTextChatInputViewController.prototype.dispose = function () {
        // disposing the communication channel listener.
        agentLEManager.offLEEvent("lpCoBrowseController:Notification", this.onCoBrowseNotification, this);
        if (this.agentTypingBouncerTimeoutRef) {
            clearTimeout(this.agentTypingBouncerTimeoutRef);
        }
        if (this.waitingForResumeTimeoutRef) {
            clearTimeout(this.waitingForResumeTimeoutRef);
        }
        ViewController.prototype.dispose.call(this);
    };

    RichTextChatInputViewController.prototype.onBlur = function () {
        if (this.waitingForResumeTimeoutRef) {
            clearTimeout(this.waitingForResumeTimeoutRef);
            this.removeClass(this.LPViewMap.chatState, this.LPClassesMap.loading);
            this.enableActionButton(this.LPActionButtonTypes.resumeConnection);
        }
    };

    RichTextChatInputViewController.prototype.setEngagementActive = function () {
        this.removeClass(this.LPViewMap.chatState, this.LPClassesMap.passiveEngagement);
        this.removeClass(this.LPViewMap.chatState, this.LPClassesMap.proactiveEngagement);
        this.addClass(this.LPViewMap.chatState, this.LPClassesMap.activeEngagement);
    };

    RichTextChatInputViewController.prototype.setEngagementInactiveCompleted = function () {
        this.removeClass(this.LPViewMap.chatState, this.LPClassesMap.activeEngagement);
        this.addClass(this.LPViewMap.chatState, this.LPClassesMap.passiveEngagement);
    };

    window.RichTextChatInputViewController = RichTextChatInputViewController;

    return RichTextChatInputViewController;

});
